import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { WorkoutReviewWizard } from './WorkoutReviewWizard';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { API_URL, getDateKey } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';
import { Exercise } from '../../types';
import { AIWorkoutWizard } from './AIWorkoutWizard';

interface RecommendationDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onServiceUnavailable?: () => void;
  onImportSuccess?: () => void;
  exercises: Exercise[];
}


interface WorkoutRecommendation {
  exercise: string;
  sets: number;
  reps: string;
  notes: string;
  alternatives?: {
    exercise: string;
    sets: number;
    reps: string;
    notes: string;
  }[];
}

interface RecommendationResponse {
  recommendation: WorkoutRecommendation[];
  message?: string;
}

export const RecommendationDialog: React.FC<RecommendationDialogProps> = ({
  open,
  onClose,
  selectedDate,
  onServiceUnavailable,
  onImportSuccess,
  exercises
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<WorkoutRecommendation[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedWorkouts, setSelectedWorkouts] = useState<Set<number>>(new Set());
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { t, language } = useLanguage();
  const { fetchWithAuth } = useAuth();

  const handleGetRecommendation = useCallback(async (customPrompt?: string, useExisting: boolean = false) => {
    if (!selectedDate) {
      setError('No date selected');
      return;
    }

    const promptToUse = customPrompt !== undefined ? customPrompt : description;

    // Save prompt if passed
    if (customPrompt !== undefined) {
      setDescription(customPrompt);
    }

    setIsLoading(true);
    setError(null);
    setRecommendation([]);
    setMessage(null);
    setSelectedWorkouts(new Set());
    setImportSuccess(null);

    try {
      const dateString = getDateKey(selectedDate);
      const descriptionParam = promptToUse.trim() ? `&description=${encodeURIComponent(promptToUse.trim())}` : '';
      const useExistingParam = useExisting ? '&useExisting=true' : '';
      const response = await fetchWithAuth(
        `${API_URL}/workouts/recommendation?date=${dateString}&language=${language}${descriptionParam}${useExistingParam}`
      );

      if (!response.ok) {
        throw new Error('Failed to get workout recommendation');
      }

      const data: RecommendationResponse = await response.json();
      setRecommendation(data.recommendation || []);
      setMessage(data.message || null);
    } catch (error: any) {
      console.error('Recommendation error:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('recommendation.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, language, description, fetchWithAuth, t, onServiceUnavailable]);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
      setRecommendation([]);
      setMessage(null);
      setSelectedWorkouts(new Set());
      setImportSuccess(null);
      setDescription('');
    }
  }, [open]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const findOrCreateExercise = async (exerciseName: string): Promise<number | null> => {
    // First, try to find existing exercise (case-insensitive)
    const existingExercise = exercises.find(
      (ex: Exercise) => ex.name.toLowerCase() === exerciseName.toLowerCase()
    );

    if (existingExercise) {
      return existingExercise.id;
    }

    // If not found, create new exercise
    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: exerciseName.trim() }),
      });

      if (response.ok) {
        const newExercise = await response.json();
        return newExercise.id;
      } else if (response.status === 409) {
        // Exercise was created by another request, try to find it again
        const refreshedExercise = exercises.find(
          (ex: Exercise) => ex.name.toLowerCase() === exerciseName.toLowerCase()
        );
        return refreshedExercise?.id || null;
      } else {
        console.error('Failed to create exercise:', exerciseName);
        return null;
      }
    } catch (error: any) {
      console.error('Error creating exercise:', exerciseName, error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      }
      return null;
    }
  };

  const handleImportSelected = async (customWorkouts?: WorkoutRecommendation[]) => {
    // If custom workouts are passed (from Wizard), use them. Otherwise fallback to selected checks (legacy logic if needed)
    const workoutsToProcess = customWorkouts || Array.from(selectedWorkouts).map(index => recommendation[index]);

    if (!selectedDate || workoutsToProcess.length === 0) {
      return;
    }

    setError(null);
    setImportSuccess(null);

    try {
      const dateString = getDateKey(selectedDate);
      let importedCount = 0;
      const failedExercises: string[] = [];

      for (const workout of workoutsToProcess) {
        if (!workout) continue;

        try {
          // Find or create the exercise
          const exerciseId = await findOrCreateExercise(workout.exercise);
          if (!exerciseId) {
            failedExercises.push(workout.exercise);
            continue;
          }

          // Parse reps (handle ranges like "8-12")
          const repsMatch = workout.reps.match(/(\d+)(?:-(\d+))?/);
          const repsValue = repsMatch ? parseInt(repsMatch[1]) : 10;

          // Create workout details from recommendation
          const workoutDetails = [{
            series: workout.sets,
            reps: repsValue,
            weight: 0 // User will fill in the weight
          }];

          // Import the workout
          const response = await fetchWithAuth(`${API_URL}/workouts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: dateString,
              exerciseId: exerciseId,
              comment: workout.notes || null,
              details: workoutDetails
            }),
          });

          if (response.ok) {
            importedCount++;
          } else {
            console.error('Failed to import workout:', workout.exercise);
            failedExercises.push(workout.exercise);
          }
        } catch (exerciseError) {
          console.error('Error importing exercise:', workout.exercise, exerciseError);
          failedExercises.push(workout.exercise);
        }
      }

      if (importedCount > 0) {
        setSelectedWorkouts(new Set()); // Clear selection after successful import

        // Notify parent component of successful import
        if (onImportSuccess) {
          onImportSuccess();
        }

        // If strictly successful (no failures), close immediately
        if (failedExercises.length === 0) {
          onClose();
        } else {
          // If partial success, show message
          const successMessage = t('recommendation.importSuccess', {
            count: importedCount,
            total: workoutsToProcess.length
          });
          setImportSuccess(successMessage);
        }
      }

      if (failedExercises.length > 0) {
        const errorMessage = t('recommendation.importPartialError', {
          failed: failedExercises.join(', ')
        });
        setError(errorMessage);
      }

    } catch (error: any) {
      console.error('Import error:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('recommendation.importError'));
      }
    } finally {
    }
  };

  const handleReset = () => {
    setRecommendation([]);
    setMessage(null);
    setDescription('');
    setError(null);
  };

  const showWizard = recommendation.length === 0 && !message;

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      sx={commonDialogStyles}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('recommendation.title')}
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {selectedDate && (
            <Typography variant="subtitle1" gutterBottom color="text.secondary" sx={{ mb: 2 }}>
              {t('recommendation.forDate', { date: formatDate(selectedDate) })}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {importSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {importSuccess}
            </Alert>
          )}

          {/* Wizard Mode */}
          {showWizard && (
            <AIWorkoutWizard
              exercises={exercises}
              onGenerate={handleGetRecommendation}
              onCancel={onClose}
              isLoading={isLoading}
            />
          )}

          {isLoading && !showWizard && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                {t('recommendation.loading')}
              </Typography>
            </Box>
          )}

          {/* Result Mode - Review Wizard */}
          {recommendation.length > 0 && !isLoading && (
            <WorkoutReviewWizard
              recommendation={recommendation}
              onSave={(editedWorkout) => handleImportSelected(editedWorkout)}
              onBack={handleReset}
              onCancel={onClose}
            />
          )}

          {/* Empty result message */}
          {message && recommendation.length === 0 && !isLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {/* Fallback close button if something goes wrong or just simply closing from wizard if we didn't pass onCancel effectively (we did though) */}
      </DialogActions>
    </Dialog>
  );
};